// src/pages/admin/Appointments.jsx
import React, { useState } from 'react';
import { useApp } from 'contexts/AppContext';
import { appointmentsService } from 'infrastructure/firebase';
import { sendAppointmentCancelled } from 'infrastructure/emailService';
import { Card, StatusBadge, Avatar, Button, Badge, EmptyState } from 'shared/components/ui';
import { useMobile } from 'shared/hooks/useMobile';
import NewAppointmentModal from 'domains/appointments/components/NewAppointmentModal';

export default function AdminAppointments() {
  const isMobile = useMobile();
  const { appointments, professionals, showToast } = useApp();
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterProfessional, setFilterProfessional] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [search, setSearch] = useState('');
  const [newModal, setNewModal] = useState(false);

  const filtered = appointments.filter(a => {
    if (filterStatus !== 'all' && a.status !== filterStatus) return false;
    if (filterProfessional && a.professionalId !== filterProfessional) return false;
    if (filterDate) {
      const apptDate = a.dateTime?.toDate?.()?.toISOString().split('T')[0] || a.date;
      if (apptDate !== filterDate) return false;
    }
    if (search) {
      const q = search.toLowerCase();
      if (!a.clientName?.toLowerCase().includes(q) && !a.procedureName?.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const handleCancel = async (id) => {
    if (!window.confirm('Cancelar este agendamento?')) return;
    try {
      const appt = appointments.find(a => a.id === id);
      await appointmentsService.cancel(id);
      if (appt?.clientEmail) {
        sendAppointmentCancelled({
          clientName:       appt.clientName,
          clientEmail:      appt.clientEmail,
          professionalName: appt.professionalName,
          procedureName:    appt.procedureName,
          date:             appt.date,
          time:             appt.time,
        }).catch(err => console.warn('[Email] Falha ao enviar cancelamento:', err));
      }
      showToast('Agendamento cancelado.');
    }
    catch { showToast('Erro ao cancelar.','danger'); }
  };

  const handleMarkDone = async (id) => {
    try { await appointmentsService.markDone(id); showToast('Marcado como realizado!'); }
    catch { showToast('Erro.','danger'); }
  };

  const selectStyle = {
    padding:'8px 36px 8px 12px', border:'1.5px solid #E8E5EC', borderRadius:10,
    fontSize:13, outline:'none', appearance:'none', minWidth:160, background:'#fff', color:'#3D3447',
    backgroundImage:"url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E\")",
    backgroundRepeat:'no-repeat', backgroundPosition:'right 10px center',
  };

  return (
    <div style={{ fontFamily:"'DM Sans',sans-serif" }}>

      {/* Page header */}
      <div style={{ marginBottom:24 }}>
        <div style={{ fontFamily:"'Instrument Serif',serif", fontSize:32, color:'#3D3447' }}>Agendamentos</div>
        <div style={{ fontSize:13, color:'#A8A1B0', marginTop:2 }}>
          Gerencie todos os agendamentos do sistema.
        </div>
      </div>

      {/* Filters */}
      <div style={{ display:'flex', gap:10, marginBottom:20, flexWrap:'wrap', alignItems:'center' }}>
        <div style={{ position:'relative', flex:1, minWidth:200, maxWidth:300 }}>
          <span style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'#A8A1B0', fontSize:14 }}>🔍</span>
          <input value={search} onChange={e=>setSearch(e.target.value)}
            placeholder="Buscar cliente ou procedimento..."
            style={{ width:'100%', padding:'8px 14px 8px 36px', border:'1.5px solid #E8E5EC', borderRadius:10, fontSize:13, outline:'none', boxSizing:'border-box', color:'#3D3447', background:'#fff' }}
          />
        </div>
        {['all','confirmed','pending','waitingConfirm','cancelled'].map(s => (
          <div key={s} onClick={()=>setFilterStatus(s)}
            style={{ padding:'6px 14px', borderRadius:20, border:`1.5px solid ${filterStatus===s?'#6B5876':'#E8E5EC'}`, fontSize:12, fontWeight:500, cursor:'pointer', background:filterStatus===s?'#6B5876':'#fff', color:filterStatus===s?'#fff':'#6B6070', transition:'all 0.15s' }}>
            {s==='all'?'Todos':s==='confirmed'?'Confirmados':s==='pending'?'Pendentes':s==='waitingConfirm'?'Aguardando':'Cancelados'}
          </div>
        ))}
        <select value={filterProfessional} onChange={e=>setFilterProfessional(e.target.value)} style={selectStyle}>
          <option value="">Todos profissionais</option>
          {professionals.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <input type="date" value={filterDate} onChange={e=>setFilterDate(e.target.value)}
          style={{ padding:'8px 12px', border:'1.5px solid #E8E5EC', borderRadius:10, fontSize:13, outline:'none', color:'#3D3447' }}
        />
        <Button variant="primary" size="sm" onClick={()=>setNewModal(true)}>+ Novo</Button>
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon="📅" title="Nenhum agendamento encontrado" text="Tente ajustar os filtros" />
      ) : isMobile ? (
        /* ── Mobile: cards ─────────────────────────────────────── */
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {filtered.map(a => {
            const pro = professionals.find(p=>p.id===a.professionalId);
            const dtDate = a.dateTime?.toDate?.() || (a.date ? new Date(`${a.date}T${a.time||'00:00'}:00`) : null);
            return (
              <Card key={a.id}>
                <div style={{ padding:'14px 16px' }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <Avatar name={a.clientName||'?'} size="sm" />
                      <strong style={{ fontSize:14, color:'#3D3447' }}>{a.clientName}</strong>
                    </div>
                    <StatusBadge status={a.status} />
                  </div>
                  <div style={{ fontSize:13, color:'#6B6070', marginBottom:4 }}>💆 {a.procedureName}</div>
                  <div style={{ fontSize:13, color:'#6B6070', marginBottom:4, display:'flex', alignItems:'center', gap:6 }}>
                    <Avatar name={pro?.name||'?'} color={pro?.avatarColor||'blue'} size="sm" />
                    {pro?.name}
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:10 }}>
                    <span style={{ fontSize:13, color:'#3D3447', fontWeight:500 }}>
                      📅 {dtDate ? dtDate.toLocaleDateString('pt-BR') : a.date} às {a.time||'—'}
                    </span>
                    <span style={{ fontWeight:700, color:'#2F5C42' }}>R$ {a.price}</span>
                  </div>
                  <div style={{ display:'flex', gap:8, marginTop:12 }}>
                    {!a.done && a.status==='confirmed' && (
                      <Button variant="success" size="sm" onClick={()=>handleMarkDone(a.id)}>✓ Realizado</Button>
                    )}
                    {a.status!=='cancelled' && (
                      <Button variant="danger" size="sm" onClick={()=>handleCancel(a.id)}>Cancelar</Button>
                    )}
                    {a.done && <Badge variant="success">✓ Realizado</Badge>}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        /* ── Desktop: tabela ───────────────────────────────────── */
        <Card>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead>
                <tr>
                  {['Cliente','Procedimento','Profissional','Data / Hora','Valor','Status','Realizado','Ações'].map((h,i)=>(
                    <th key={i} style={{ textAlign:'left', padding:'10px 16px', fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:1, color:'#A8A1B0', borderBottom:'1px solid #E8E5EC', background:'#FAF7FB', whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(a => {
                  const pro = professionals.find(p=>p.id===a.professionalId);
                  const dtDate = a.dateTime?.toDate?.() || (a.date ? new Date(`${a.date}T${a.time||'00:00'}:00`) : null);
                  return (
                    <tr key={a.id}>
                      <td style={{ padding:'13px 16px', borderBottom:'1px solid #E8E5EC' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <Avatar name={a.clientName||'?'} size="sm" />
                          <strong style={{ color:'#3D3447' }}>{a.clientName}</strong>
                        </div>
                      </td>
                      <td style={{ padding:'13px 16px', borderBottom:'1px solid #E8E5EC', color:'#6B6070' }}>{a.procedureName}</td>
                      <td style={{ padding:'13px 16px', borderBottom:'1px solid #E8E5EC', color:'#6B6070' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                          <Avatar name={pro?.name||'?'} color={pro?.avatarColor||'blue'} size="sm" />
                          {pro?.name}
                        </div>
                      </td>
                      <td style={{ padding:'13px 16px', borderBottom:'1px solid #E8E5EC' }}>
                        <span style={{ fontWeight:500, color:'#3D3447' }}>{dtDate ? dtDate.toLocaleDateString('pt-BR') : a.date||'—'}</span>
                        <span style={{ color:'#A8A1B0', marginLeft:6 }}>{a.time||(dtDate?dtDate.toLocaleTimeString('pt-BR',{hour:'2-digit',minute:'2-digit'}):'')}  </span>
                      </td>
                      <td style={{ padding:'13px 16px', borderBottom:'1px solid #E8E5EC', fontWeight:600, color:'#2F5C42' }}>R$ {a.price}</td>
                      <td style={{ padding:'13px 16px', borderBottom:'1px solid #E8E5EC' }}><StatusBadge status={a.status} /></td>
                      <td style={{ padding:'13px 16px', borderBottom:'1px solid #E8E5EC' }}>
                        {a.done ? <Badge variant="success">✓ Sim</Badge> : <Badge variant="neutral">Não</Badge>}
                      </td>
                      <td style={{ padding:'13px 16px', borderBottom:'1px solid #E8E5EC' }}>
                        <div style={{ display:'flex', gap:4 }}>
                          {!a.done && a.status==='confirmed' && <Button variant="success" size="sm" onClick={()=>handleMarkDone(a.id)}>✓</Button>}
                          {a.status!=='cancelled' && <Button variant="danger" size="sm" onClick={()=>handleCancel(a.id)}>🗑️</Button>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {newModal && <NewAppointmentModal onClose={()=>setNewModal(false)} />}
    </div>
  );
}
